export const SUPPORT_BRIEFING = {
  greeting: {
    date: 'Monday, Jan 27',
    meetings: 2,
    tasks: 4,
    ticketAlerts: 3,
    lucMessage: "Hey Maya, heads up — the v2.3 release is still generating ticket volume. Zira flagged 5 accounts at churn risk and your SLA compliance dipped to 74%. Let's triage the critical ones first."
  },
  tickets: [
    {
      id: 'tk1',
      title: 'Acme Corp — API integration broken after v2.3',
      agent: 'zira',
      priority: 'critical',
      alert: true,
      account: 'Acme Corp',
      slaStatus: 'breached',
      timeOpen: '6 hours',
      description: 'Customer reports API endpoints returning 500 errors since the v2.3 update. This is a $48K annual account.',
      expanded: {
        summary: 'Acme Corp\'s API integration broke after the v2.3 deployment. Their CTO has escalated directly. This is one of our largest accounts and is up for renewal in 45 days.',
        details: [
          { label: 'Account ARR', value: '$48,000' },
          { label: 'Renewal Date', value: 'March 12 (45 days)' },
          { label: 'Priority', value: 'Critical (P1)' },
          { label: 'SLA Status', value: 'Breached (4hr target, 6hr elapsed)' },
          { label: 'Affected Feature', value: 'REST API v2 endpoints' },
          { label: 'Root Cause', value: 'Auth token format change in v2.3' },
        ],
        thinking: 'Zira traced the issue to a breaking change in the auth token format introduced in v2.3. This affects all customers using the REST API v2 endpoints. Engineering has confirmed a hotfix is in progress. ETA: 2 hours. Zira has pre-drafted a customer communication.',
        proposedActions: [
          { label: 'Send proactive update to Acme Corp CTO', agent: 'Zira' },
          { label: 'Escalate hotfix priority to engineering', agent: 'Zira' },
          { label: 'Prepare rollback plan if hotfix delayed', agent: 'Zira' },
        ]
      }
    },
    {
      id: 'tk2',
      title: 'DataFlow — Dashboard loading timeout',
      agent: 'zira',
      priority: 'high',
      alert: true,
      account: 'DataFlow',
      slaStatus: 'at-risk',
      timeOpen: '3 hours',
      description: 'Dashboard takes 30+ seconds to load for accounts with 10K+ records. 4 customers affected.',
      expanded: {
        summary: 'Performance regression in v2.3 affecting dashboard load times for high-volume accounts. DataFlow is the most vocal but 3 other accounts have reported the same issue.',
        details: [
          { label: 'Affected Accounts', value: '4 (DataFlow, TechCorp, NovaTech, Meridian)' },
          { label: 'Combined ARR', value: '$142,000' },
          { label: 'SLA Status', value: 'At risk (2hr remaining)' },
          { label: 'Avg Load Time', value: '34 seconds (was 3 seconds)' },
          { label: 'Root Cause', value: 'Unindexed query in v2.3 migration' },
        ],
        thinking: 'Zira correlated 4 separate tickets to the same root cause — a missing database index after the v2.3 migration. Engineering confirmed the fix is straightforward (15 min deploy). Recommends bundling communication to all 4 affected accounts.',
        proposedActions: [
          { label: 'Bundle response to all 4 affected accounts', agent: 'Zira' },
          { label: 'Request index fix deployment', agent: 'Zira' },
          { label: 'Schedule follow-up checks post-fix', agent: 'Zira' },
        ]
      }
    },
    {
      id: 'tk3',
      title: 'Meridian Health — Onboarding support request',
      agent: 'zira',
      priority: 'medium',
      alert: false,
      account: 'Meridian Health',
      slaStatus: 'on-track',
      timeOpen: '1 hour',
      description: 'New customer requesting onboarding walkthrough. Scheduled for tomorrow but asked if it can be moved up.',
      expanded: {
        summary: 'Meridian Health signed last week and is eager to start. Their VP Ops has availability today at 3 PM. Zira recommends accommodating to build early goodwill.',
        details: [
          { label: 'Account ARR', value: '$24,000 (new)' },
          { label: 'Requested Time', value: 'Today, 3:00 PM' },
          { label: 'Original Schedule', value: 'Tomorrow, 10:00 AM' },
          { label: 'Contact', value: 'Anita Desai (VP Ops)' },
        ],
        thinking: 'Zira checked the calendar — the 3 PM slot is open. Early onboarding correlates with 23% higher 90-day retention in our data. Recommends moving it up.'
      }
    }
  ],
  tasks: {
    today: [
      {
        id: 'sut1',
        title: 'Triage v2.3 critical tickets',
        agent: 'zira',
        urgency: 'critical',
        description: 'Zira has grouped 23 tickets by root cause. 3 clusters need your prioritization decision.',
        expanded: {
          summary: 'Zira analyzed all 23 open tickets from the v2.3 release and grouped them into 3 clusters: API auth failures (8 tickets), dashboard performance (7 tickets), and webhook delivery delays (8 tickets).',
          details: [
            { label: 'Total Open Tickets', value: '23' },
            { label: 'Cluster 1', value: 'API auth failures (8 tickets, P1)' },
            { label: 'Cluster 2', value: 'Dashboard performance (7 tickets, P2)' },
            { label: 'Cluster 3', value: 'Webhook delays (8 tickets, P3)' },
          ],
          thinking: 'Zira recommends prioritizing API auth first (highest ARR impact at $89K), then dashboard performance ($142K ARR but lower severity), then webhooks (workaround available).'
        }
      },
      {
        id: 'sut2',
        title: 'Review churn risk outreach drafts',
        agent: 'zira',
        urgency: 'due-today',
        description: 'Zira drafted personalized outreach for 5 accounts flagged for churn risk. Need your approval before sending.',
        expanded: {
          summary: 'Zira has prepared tailored emails for each of the 5 churn-risk accounts, addressing their specific issues and offering resolution timelines.',
          details: [
            { label: 'Accounts', value: '5 (combined ARR: $89K)' },
            { label: 'Renewals < 60 days', value: '3 accounts' },
            { label: 'Draft Status', value: 'Ready for review' },
          ],
          artifacts: [
            { name: 'Outreach Drafts', type: 'pdf' },
            { name: 'Churn Risk Scorecard', type: 'spreadsheet' },
          ]
        }
      },
      {
        id: 'sut3',
        title: 'Approve known-issues page update',
        agent: 'zira',
        description: 'Zira updated the public status page with v2.3 known issues and ETAs. Needs your sign-off before publishing.',
        expanded: {
          summary: 'The status page update covers all 3 known v2.3 issues with customer-friendly descriptions, workarounds, and resolution ETAs.',
          artifacts: [
            { name: 'Status Page Draft', type: 'pdf' },
          ],
          thinking: 'Publishing this proactively should reduce inbound ticket volume by an estimated 30-40% based on past incidents. Zira has also prepared a banner notification for the app dashboard.'
        }
      }
    ],
    ifTime: [
      { id: 'sut4', title: 'Review CSAT survey responses', agent: 'dora', description: 'Dora compiled this week\'s CSAT responses. Overall score 4.1/5 but 3 accounts left negative feedback.' },
      { id: 'sut5', title: 'Update support playbook for v2.3 issues', agent: 'zira', description: 'Zira suggests adding troubleshooting guides for the 3 common v2.3 issues to the internal knowledge base.' }
    ]
  },
  supportMetrics: {
    snapshot: [
      { label: 'SLA Compliance', value: '74%', trend: '-18%', alert: true },
      { label: 'Open Tickets', value: '23', trend: '+40%', alert: true },
      { label: 'Avg Response Time', value: '4.2 hrs', trend: '+2.1 hrs', alert: true },
      { label: 'CSAT Score', value: '4.1 / 5', trend: '-0.3' },
      { label: 'First Contact Resolution', value: '62%', trend: '-8%' },
    ],
    alert: {
      id: 'sa1',
      title: '5 accounts at churn risk — $89K ARR exposed',
      agents: ['zira', 'sally'],
      severity: 'critical',
      description: 'All 5 churn-risk accounts have open tickets from v2.3. 3 are up for renewal within 60 days.',
      expanded: {
        details: [
          { label: 'At-Risk ARR', value: '$89,000 (5 accounts)' },
          { label: 'Renewals < 60 days', value: '3 accounts ($62K)' },
          { label: 'Common Issue', value: 'v2.3 release — unresolved tickets' },
          { label: 'Avg Ticket Age', value: '4.2 days' },
          { label: 'Escalation Status', value: '2 accounts escalated to VP' },
        ],
        thinking: 'Zira cross-referenced ticket data with renewal dates and account health scores. The 3 accounts renewing within 60 days are the highest priority — any unresolved ticket at renewal is a 4x churn risk multiplier. Sally has been notified about the revenue exposure.',
        proposedActions: [
          { label: 'Send personalized outreach to all 5 accounts', agent: 'Zira' },
          { label: 'Fast-track ticket resolution for 3 renewal accounts', agent: 'Zira' },
          { label: 'Alert sales team about renewal risk', agent: 'Sally' },
        ]
      }
    }
  },
  meetings: [
    { id: 'sum1', title: 'Support team standup', time: '9:30 AM', duration: '15 min', with: 'Support Team', type: 'internal', agenda: ['v2.3 ticket triage update', 'SLA breach review', 'Priority assignments for today'] },
    { id: 'sum2', title: 'Incident review with Engineering', time: '11:00 AM', duration: '30 min', with: 'Engineering Team', type: 'internal', agenda: ['v2.3 hotfix status', 'Root cause analysis', 'Timeline for remaining fixes'] },
  ],
  dailyTimeline: [
    { time: '9:00 AM', type: 'task', title: 'Triage v2.3 critical tickets', agent: 'zira' },
    { time: '9:30 AM', type: 'meeting', title: 'Support team standup', duration: '15 min', with: 'Support Team' },
    { time: '10:00 AM', type: 'task', title: 'Review churn risk outreach drafts', agent: 'zira' },
    { time: '10:30 AM', type: 'task', title: 'Approve known-issues page update', agent: 'zira' },
    { time: '11:00 AM', type: 'meeting', title: 'Incident review with Engineering', duration: '30 min', with: 'Engineering Team' },
    { time: '2:00 PM', type: 'task', title: 'Follow up on Acme Corp escalation', agent: 'zira' },
    { time: '3:00 PM', type: 'task', title: 'Meridian Health onboarding (moved up)', agent: 'zira' },
  ],
  inbox: [
    { id: 'sui1', sender: 'James Park (TechCorp CTO)', senderInitials: 'JP', time: '7:55 AM', summary: 'Escalating API issue — production systems affected, requesting urgent call.', expandedSummary: 'James reports their production integration is returning 500 errors since the v2.3 update. His team has been debugging for 6 hours and is now escalating formally. He wants a call within the hour and a written incident report by EOD. The account is worth $48K ARR and renewal is in 45 days.' },
    { id: 'sui2', sender: 'Lisa Chen (DataFlow VP Ops)', senderInitials: 'LC', time: '7:30 AM', summary: 'Dashboard loading extremely slow — 4 team members affected, asking for ETA.', expandedSummary: 'Lisa says the dashboard has been unusable for her team since Friday. Load times exceed 30 seconds for their 12K-record dataset. She needs an ETA for the fix and wants to know if there\'s a workaround. Her tone is measured but firm — this is impacting their daily operations.' },
    { id: 'sui3', sender: 'Raj Nair (Engineering Lead)', senderInitials: 'RN', time: '7:15 AM', summary: 'v2.3 hotfix for API auth is in code review — ETA 2 hours to production.', expandedSummary: 'Raj confirmed the root cause: auth token format change in v2.3 broke backwards compatibility. The hotfix preserves both old and new formats. Currently in code review, should merge within the hour, and deploy after a 30-minute canary. He\'s asking if any customers need a direct heads-up before the deploy window.' },
    { id: 'sui4', sender: 'Anita Desai (Meridian Health)', senderInitials: 'AD', time: 'Yesterday', summary: 'Asking if onboarding can be moved up to today — her team has availability at 3 PM.', expandedSummary: 'Anita\'s team cleared their afternoon and she\'s asking if the onboarding session can be moved from tomorrow to today at 3 PM. She\'s eager to get started and has already pre-read the setup documentation. The 3 PM slot is open on your calendar.' },
    { id: 'sui5', sender: 'Neel Kapoor (Finance)', senderInitials: 'NK', time: 'Yesterday', summary: 'Asking for churn risk data to factor into Q1 revenue projections.', expandedSummary: 'Neel needs the latest churn risk assessment for the cashflow model. Specifically: which accounts are at risk, their combined ARR, renewal dates, and your confidence level on retention. He wants to model a worst-case scenario for the board presentation. Requesting data by tomorrow morning.' },
  ]
};
